import type { UseQueryResult } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import type { CleaningTaskDetailResponse } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { Button } from '@/shared/components';

/**
 * Räume einer Aufgabe im Blatt: Foto da oder offen. Lädt die Aufgabe noch oder scheiterte sie,
 * steht das da – der Abschnitt verschwindet nicht still. Ohne Raumliste fehlt er.
 */
export function TaskRoomsSection({
  detail,
}: {
  detail: UseQueryResult<CleaningTaskDetailResponse>;
}) {
  const problem = useApiProblem(detail.error);
  const section = 'border-hairline border-t pt-4';
  if (detail.isPending)
    return (
      <p role="status" className={`${section} text-ink-soft text-sm`}>
        Räume werden geladen …
      </p>
    );
  if (!detail.data)
    return (
      <div role="alert" className={`${section} flex flex-wrap items-center gap-3`}>
        <p className="min-w-0 flex-1 text-sm">
          {problemText(problem, 'Die Räume konnten nicht geladen werden.')}
        </p>
        <Button variant="secondary" className="min-h-11" onClick={() => void detail.refetch()}>
          Erneut laden
        </Button>
      </div>
    );
  const { items, photos } = detail.data;
  if (items.length === 0) return null;
  return (
    <section className={section}>
      <h3 className="font-serif text-lg font-bold">Räume</h3>
      <ul className="mt-2 flex flex-col gap-1.5">
        {items.map((item) => {
          const filled = photos.some((photo) => photo.checklistItemId === item.id);
          return (
            <li
              key={item.id}
              className="border-hairline rounded-control flex min-h-11 items-center gap-3 border px-3 text-sm"
            >
              <CheckCircle2 className={filled ? 'text-forest' : 'text-ink-faint'} size={18} />
              <span className="flex-1">{item.text}</span>
              <span className="text-ink-soft text-xs">{filled ? 'Foto da' : 'offen'}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
